// Centralised parser for errors coming back from the API.
// The backend always returns:
//   { success: false, error: { code, message, errors?: { Field: [..] } } }
// RTK Query exposes this payload as `err.data` and the HTTP status as `err.status`.

/**
 * Turn an RTK Query error (or any thrown error) into a human readable string.
 * Includes field-level messages so the user knows EXACTLY what to fix.
 */
export function apiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;

  // Network / fetch failure
  if (err.status === 'FETCH_ERROR') return 'Could not reach the server. Check your connection or the backend.';
  if (err.status === 'PARSING_ERROR') return 'Server response could not be parsed.';
  if (err.status === 'TIMEOUT_ERROR') return 'The request timed out.';
  if (err.status === 0) return 'Network error — server is unreachable.';

  // RTK Query response body
  const body = err.data ?? err;
  const apiErr = body?.error;

  if (apiErr?.message) {
    const fields = formatFieldErrors(apiErr.errors);
    return fields ? `${apiErr.message} — ${fields}` : apiErr.message;
  }

  // Status-only fallbacks
  if (err.status === 401) return 'Your session has expired. Please log in again.';
  // Prefer the server's specific reason for a 403; only fall back to a neutral phrase (never the
  // old repetitive "You do not have permission to do this." nag) when the server sent nothing.
  if (err.status === 403) return body?.error?.message || body?.message || 'This action is not available for your account.';
  if (err.status === 404) return 'Not found.';
  if (err.status === 409) return body?.error?.message || 'This conflicts with existing data.';
  if (err.status === 429) return 'Too many requests — please slow down and try again.';
  if (typeof err.status === 'number' && err.status >= 500) return 'Server error. Please try again or contact support.';

  return body?.message || err.message || fallback;
}

/** True for permission-denied responses (403). Used to silently skip benign "you can't do that"
 *  toasts in quick-edit workflows where a restricted role simply shouldn't be nagged. */
export function isPermissionError(err) {
  return err?.status === 403 || err?.originalStatus === 403 || err?.data?.error?.code === 'Forbidden';
}

function formatFieldErrors(errors) {
  if (!errors) return null;
  const entries = Object.entries(errors);
  if (entries.length === 0) return null;
  return entries
    .map(([field, msgs]) => {
      const label = humanField(field);
      const text = Array.isArray(msgs) ? msgs.join('; ') : String(msgs);
      return `${label}: ${text}`;
    })
    .join(' · ');
}

function humanField(name) {
  if (!name || name === '_') return 'Request';
  // PascalCase / camelCase → spaced words
  return name
    .replace(/\.[A-Za-z]/g, (s) => ' › ' + s.slice(1))    // "Items.Quantity" → "Items › Quantity"
    .replace(/([A-Z][a-z])/g, ' $1')                       // "ScheduledAt" → " Scheduled At"
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')             // "URLPath" → "URL Path"
    .trim();
}

/**
 * Return a { fieldName: 'message' } map suitable for react-hook-form's setError().
 * Keys are camelCased to match the form's field names.
 */
export function apiFieldErrors(err) {
  const body = err?.data ?? err;
  const errors = body?.error?.errors;
  if (!errors) return null;
  const out = {};
  Object.entries(errors).forEach(([key, msgs]) => {
    const camel = toCamel(key);
    out[camel] = Array.isArray(msgs) ? msgs.join('; ') : String(msgs);
  });
  return out;
}

function toCamel(s) {
  if (!s || s === '_') return s;
  // "ScheduledAt" → "scheduledAt" ; nested "Items[0].Quantity" → "items[0].quantity"
  return s.replace(/^[A-Z]+/, (m) => m.toLowerCase());
}

/**
 * Push field-level errors from the API into react-hook-form's state so they
 * appear next to the right field automatically.
 *
 * @param {Function} setError — the `setError` from useForm()
 * @param {object} err — the RTK Query error
 * @returns {string|null} an unmatched/global error message, if any
 */
export function applyApiErrorsToForm(setError, err) {
  const fieldErrors = apiFieldErrors(err);
  if (fieldErrors) {
    Object.entries(fieldErrors).forEach(([field, message]) => {
      setError(field, { type: 'server', message });
    });
  }
  // Always return the headline message so the caller can show a banner
  return apiErrorMessage(err);
}
